/**
 * 节点内容替换：把选择的文件写入已有节点，保留节点位置与连线。
 *
 * - 第一个文件替换目标节点内容（远程上传，乐观预览，完成后换持久网关地址）
 * - 其余文件在目标节点附近新建图片节点（40px 阶梯排布）
 */
import toast from '../../../components/Toast';
import { boardActions, getBoardState } from '../state/boardStore';
import { makeNode } from '../utils';
import { uploadFile, bindUploadToNode, type RemoteUploadHandle } from '../imageStorage';
import { toPersistableMediaUrl } from './persistableMediaUrl';
import type { CanvasNode, ImageNodeData } from '../types';

export async function replaceImageNodeWithFiles(
  target: CanvasNode<ImageNodeData>,
  files: File[]
): Promise<void> {
  const images = files.filter((f) => f.type.startsWith('image/'));
  if (images.length === 0) {
    toast.error('请选择图片文件');
    return;
  }

  // 第一个文件替换目标节点（位置与连线保持不变）：先展示本地预览，远程完成后换持久地址。
  // 上传失败回滚原引用——不回滚的话原图已被 blob 预览覆盖，刷新后即死链
  try {
    const first = uploadFile(images[0], getBoardState().projectId ?? undefined);
    const prevImage = {
      imageUrl: target.data.imageUrl,
      _imageStorageKey: (target.data._imageStorageKey ?? null) as string | null,
    };
    boardActions.updateNodeData(target.id, {
      imageUrl: first.previewUrl,
      _imageStorageKey: null,
      status: 'completed',
      errorMessage: null,
    });
    void first.settled.then(
      (remoteUrl) => {
        const persistable = toPersistableMediaUrl(remoteUrl) ?? remoteUrl;
        boardActions.updateNodeData(target.id, { imageUrl: persistable, _imageStorageKey: null });
        URL.revokeObjectURL(first.previewUrl);
      },
      () => {
        if (getBoardState().nodes.some((n) => n.id === target.id)) {
          boardActions.updateNodeData(target.id, {
            ...prevImage,
            status: 'completed',
            errorMessage: null,
          });
        }
        URL.revokeObjectURL(first.previewUrl);
        toast.error('图片上传失败，已恢复原图');
      }
    );
  } catch (err) {
    toast.error(err instanceof Error ? err.message : '图片导入失败');
    return;
  }

  // 其余文件在附近新建节点（40px 阶梯排布）
  const rest = images.slice(1);
  for (let i = 0; i < rest.length; i++) {
    try {
      const handle: RemoteUploadHandle = uploadFile(rest[i], getBoardState().projectId ?? undefined);
      const node = makeNode(
        'image',
        { x: target.x + 40 * (i + 1), y: target.y + 40 * (i + 1) },
        {
          imageUrl: handle.previewUrl,
          status: 'completed',
        } as Partial<ImageNodeData> as ImageNodeData
      );
      boardActions.addNode(node);
      bindUploadToNode(node.id, handle);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : '图片导入失败');
    }
  }
}
